import { forwardRef, useCallback, useMemo } from "react";
import { OptionTypeResponse, SideResponse } from "../../../codegen-api";
import usePrevious from "../../../hooks/usePrevious";
import { useOptionsChainTableHeaders } from "./headers";
import RowBoth from "./RowBoth";
import RowSingle from "./RowSingle";
import { IRowProps } from "./types";

// ====================
//    TABLE CONTENT
// ====================

const Row = forwardRef<any, IRowProps>(
  (
    {
      row,
      onTrade,
      totalCallsPutsSize,
      sideTableWidth,
      strikeWidth,
      activeInstrumentId,
      mode,
      decimals,
    },
    ref
  ) => {
    const { bidHeaders, headers } = useOptionsChainTableHeaders();

    // Store ref to previous prop
    const prevRow = usePrevious(row);
    const needsAnimation = useMemo(() => {
      const defaultValues = {
        call: {
          bid: false,
          ask: false,
        },
        put: {
          bid: false,
          ask: false,
        },
      };
      if (prevRow) {
        return {
          call: {
            bid:
              Number(prevRow.call.bid.volume) !== Number(row.call.bid.volume),
            ask:
              Number(prevRow.call.ask.volume) !== Number(row.call.ask.volume),
          },
          put: {
            bid: Number(prevRow.put.bid.volume) !== Number(row.put.bid.volume),
            ask: Number(prevRow.put.ask.volume) !== Number(row.put.ask.volume),
          },
        };
      }
      return defaultValues;
    }, [prevRow, row]);

    const activeCellType: OptionTypeResponse | undefined = useMemo(() => {
      if (activeInstrumentId) {
        if (activeInstrumentId === row.call.instrumentName) {
          return OptionTypeResponse.Call;
        }
        if (activeInstrumentId === row.put.instrumentName) {
          return OptionTypeResponse.Put;
        }
      }
      return undefined;
    }, [activeInstrumentId, row]);

    const computeIV = useCallback(
      (type: OptionTypeResponse, side: SideResponse): string => {
        const bidAsk = side === SideResponse.Buy ? "bid" : "ask";
        if (Number(row[type][bidAsk]?.iv) !== 0) {
          return `${(Number(row[type][bidAsk]?.iv) * 100).toFixed(2)}%`;
        }

        return "-";
      },
      [row]
    );

    if (mode === "both") {
      return (
        <RowBoth
          ref={ref}
          row={row}
          onTrade={onTrade}
          totalCallsPutsSize={totalCallsPutsSize}
          sideTableWidth={sideTableWidth}
          strikeWidth={strikeWidth}
          decimals={decimals}
          activeCellType={activeCellType}
          needsAnimation={needsAnimation}
          computeIV={computeIV}
          bidHeaders={bidHeaders}
          headers={headers}
        />
      );
    }

    return (
      <RowSingle
        ref={ref}
        row={row}
        onTrade={onTrade}
        totalCallsPutsSize={totalCallsPutsSize}
        sideTableWidth={sideTableWidth}
        strikeWidth={strikeWidth}
        decimals={decimals}
        mode={mode}
        activeCellType={activeCellType}
        needsAnimation={needsAnimation}
        computeIV={computeIV}
        bidHeaders={bidHeaders}
        headers={headers}
      />
    );
  }
);

export default Row;
